import React, { useState, useEffect } from "react";
import { css } from '@emotion/css';
import { color_colors_decline, color_colors_growth, color_colors_ocean, color_shades_dark, color_text_default, color_text_light, pastel_green, pastel_red, silver_four } from "../../../constants/colors";
import LanguageModel from "../../../models/features/api/LanguageModel";
import ToggleIndicator from "../../../components/general/ToggleIndicator";
import Button from "../../../components/general/Button";
import HorizontalSeparator from "../../../components/structure/HorizontalSeparator";
import UserApplicationPreferencesContainer from "../../../state/containers/UserApplicationPreferencesContainer";
import { SimulatorMessage } from "./SimulatorInteraction";
import CustomScrollbars from "../../../components/structure/CustomScrollbars";
import useWindowSize from "../../../hooks/UseWindowSize";
import styled from "@emotion/styled";
import TestCaseContainer, { TestCaseReference } from "../../../hooks/TestCaseContainer";
import TestCase from "../../../models/customAssistant/TestCase";
import TestCaseEditingModal from "../../../components/forms/EditTestCase";
import SecondaryLoader from "../../../components/general/SecondaryLoader";
import set from 'lodash';
import SearchField from "../../../components/forms/SearchField";
import CheckboxField from "../../../components/forms/CheckboxField";
import TooltipWrapper from "../../../components/general/TooltipWrapper";
import DeleteConfirmation from "../../featureContent/components/DeleteConfirmation";
import ConfirmationDialog from "../../../components/structure/ConfirmationDialog";

const removeIcon = require("../../../content/images/ssml/remove-circle-dark.svg");
const copyIcon = require("../../../content/images/content-explorer/copy.svg");
const arrowRight = require("../../../content/images/arrow-circle-right-dark.svg");
const refresh = require("../../../content/images/refresh-dark.svg");
const greenPowerIcon = require("../../../content/images/simulator/green-power.svg");

const isSameTestCase = (first: TestCaseReference, second: TestCaseReference) =>
    first.name === second.name && (first.contentItemId ?? null) === (second.contentItemId ?? null);

interface SimulatorTestCasesPanelProps {
    applicationId: string,
    contentItemId?: string,
    userApplicationPreferencesContainer: UserApplicationPreferencesContainer,
    languages: LanguageModel[]
    selectedLanguage?: LanguageModel,
    requests?: SimulatorMessage[],
    handleSendWelcome: () => void
    handleStartNewSession: () => void
    simulateTestCase: (testCase: TestCase) => void
};

const SimulatorTestCasesPanel: React.FC<SimulatorTestCasesPanelProps> = (props) => {
    const windowSize = useWindowSize();
    const testCaseContainer = TestCaseContainer.useContainer();
    const [testCase, setTestCase] = useState<TestCase>(null);
    const [checkedCases, setCheckedCases] = useState<TestCaseReference[]>([]);
    const [search, setSearch] = useState<string>("");
    const [nameToDelete, setNameToDelete] = useState<string>("");
    const [contentItemIdToDelete, setContentItemIdToDelete] = useState("")
    const [casesToDelete, setCasesToDelete] = useState<TestCaseReference[]>([]);
    const [deleteConfirmationText, setDeleteConfirmationText] = useState("");
    const [bulkDeleteError, setBulkDeleteError] = useState("");
    const [isBulkDeleting, setIsBulkDeleting] = useState(false);

    useEffect(() => {
        if (props.contentItemId?.length > 0) {
            testCaseContainer.loadAppLevelTestCase(props.applicationId, props.contentItemId)
        }
        else {
            testCaseContainer.loadAppLevelTestCases(props.applicationId);
        }
    }, []);

    useEffect(() => {
        const runTestCaseSimulation = async () => {
            if (sessionStorage.getItem("testCaseToSimulate") !== null) {
                const testCase = JSON.parse(sessionStorage.getItem("testCaseToSimulate"));
                await simulateCase(testCase.name, testCase?.contentItemId);
                sessionStorage.removeItem("testCaseToSimulate");
            }
        };

        runTestCaseSimulation();
    }, []);


    const confirmDelete = async () => {
        if (nameToDelete) {
            const selectedTestCase = { name: nameToDelete, contentItemId: contentItemIdToDelete };
            const failed = await testCaseContainer.deleteAppLevelTestCases(props.applicationId, [selectedTestCase]);
            if (!failed.length) {
                setCheckedCases(current => current.filter(testCase => !isSameTestCase(testCase, selectedTestCase)));
            }
            setNameToDelete("");
            await testCaseContainer.loadAppLevelTestCases(props.applicationId);
        }
    }

    const closeBulkDeleteConfirmation = () => {
        if (isBulkDeleting) return;
        setCasesToDelete([]);
        setDeleteConfirmationText("");
        setBulkDeleteError("");
    }

    const confirmBulkDelete = async () => {
        if (isBulkDeleting || deleteConfirmationText !== "DELETE" || !casesToDelete.length) return;

        setIsBulkDeleting(true);
        setBulkDeleteError("");
        try {
            const failed = await testCaseContainer.deleteAppLevelTestCases(props.applicationId, casesToDelete);
            const deleted = casesToDelete.filter(testCase => !failed.some(item => isSameTestCase(item, testCase)));
            setCheckedCases(current => current.filter(testCase => !deleted.some(item => isSameTestCase(item, testCase))));
            await testCaseContainer.loadAppLevelTestCases(props.applicationId);

            if (failed.length) {
                setCasesToDelete(failed);
                setDeleteConfirmationText("");
                setBulkDeleteError(`${failed.length} test case${failed.length === 1 ? "" : "s"} could not be deleted. You can retry or cancel.`);
            } else {
                setCasesToDelete([]);
                setDeleteConfirmationText("");
                setBulkDeleteError("");
            }
        } catch (error) {
            setBulkDeleteError("Unable to delete the selected test cases. Please try again.");
        } finally {
            setIsBulkDeleting(false);
        }
    }

    const getContentItemFromSessionAttributes = (testCase) => {
        const turns = testCase?.sessionState?.sessionAttributes?.["conversationTurnHistoryState"]?.["TurnHistory"];
        const contentItemId = Array.isArray(turns) && turns.length > 0
            ? turns[turns.length - 1]?.["ContentId"]
            : null

        return contentItemId
    }

    const closeDeleteConfirmation = () => {
        setNameToDelete("");
    }

    const simulateCase = async (name: string, contentItemId?: string) => {
        const testCase = await testCaseContainer.loadAppLevelTestCase(props.applicationId, name, contentItemId);
        if (testCase) {
            props.simulateTestCase(testCase);
        }
    }

    const editTestCase = async (name: string, contentItemId?: string) => {
        const testCase = await testCaseContainer.loadAppLevelTestCase(props.applicationId, name, contentItemId);
        if (testCase) {
            setTestCase(testCase);
        }
    }

    const getTestCaseColor = (name: string) => {
        const testCaseResults = testCaseContainer.testCaseResults;
        if (testCaseResults && testCaseResults[name]) {
            if (Object.keys(testCaseResults[name]).some(k => testCaseResults[name][k]?.toLowerCase() !== "true"))
                return "red";
            return "green";
        }
        else
            return "";
    }

    const isLoadingCase = (name: string) => {
        if (testCaseContainer.runningTests?.length && testCaseContainer.runningTests.includes(name)) {
            const testCaseResults = testCaseContainer.testCaseResults;
            if (testCaseResults && testCaseResults[name]) {
                return false;
            }
            return true;
        } else {
            return false;
        }
    }

    const handleSearch = (searchEvent: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(searchEvent.target.value);
    };

    const updateAvailableTestCases = (newTestCase: TestCase) => {
        testCaseContainer.createTestCase(props.applicationId, newTestCase);
        if (!(newTestCase?.contentItemId?.length > 0)) {
            const newAvailableTestCases = {
                ...testCaseContainer.availableTestCases,
                applicationTestCases: [...testCaseContainer.availableTestCases?.applicationTestCases, newTestCase.name]
            }
            testCaseContainer.setAvailableTestCases(newAvailableTestCases);
        }
        else {
            const newAvailableTestCases = {
                ...testCaseContainer.availableTestCases,
                contentItemTestCases: {
                    ...testCaseContainer.availableTestCases.contentItemTestCases,
                    [newTestCase.contentItemId]: [...testCaseContainer.availableTestCases?.contentItemTestCases[newTestCase.contentItemId], newTestCase.name]
                }
            }
            testCaseContainer.setAvailableTestCases(newAvailableTestCases);
        }
    }

    const allTestCases = testCaseContainer.gatherAllTestCases();
    const allTestCaseIds = testCaseContainer.getContentItemIdList(testCaseContainer?.availableTestCases?.applicationTestCases?.length ?? 0, testCaseContainer?.availableTestCases?.contentItemTestCases ?? {});
    const visibleTestCases: TestCaseReference[] = allTestCases
        .map((name, index) => ({ name, contentItemId: allTestCaseIds[index] }))
        .filter(testCase => testCase.name.toLowerCase().includes(search.toLowerCase()))
        .sort((a, b) => a.name.localeCompare(b.name));
    const testCases = visibleTestCases.map(testCase => testCase.name);
    const testCaseIds = visibleTestCases.map(testCase => testCase.contentItemId);
    return (
        <div className={containerStyle}>
            <TestCaseHeadingWrapper>
                <div className="title-container">
                    <div className="title">Test Cases</div>
                </div>
                <RestartButton themes={["white-pill"]} text="Restart Session" onClick={() => props.handleStartNewSession()} />
                <StyledTopButton themes={["white-pill"]} text="Run All" onClick={() => testCaseContainer.runAppLevelTestCases()} />
                {checkedCases.length ?
                    <StyledTopButton themes={["white-pill"]} text="Run Checked" onClick={() => testCaseContainer.runAppLevelTestCases(checkedCases)} />
                    : <></>}
                {checkedCases.length ?
                    <StyledTopButton themes={["white-pill"]} text={`Delete Checked (${checkedCases.length})`} onClick={() => {
                        setCasesToDelete([...checkedCases]);
                        setDeleteConfirmationText("");
                        setBulkDeleteError("");
                    }} />
                    : <></>}
                <SearchRow>
                    <SearchCheckbox
                        checked={visibleTestCases.length > 0 && visibleTestCases.every(testCase => checkedCases.some(selected => isSameTestCase(selected, testCase)))}
                        onClick={(e) => {
                            if (visibleTestCases.length) {
                                if (visibleTestCases.every(testCase => checkedCases.some(selected => isSameTestCase(selected, testCase)))) {
                                    setCheckedCases(current => current.filter(selected => !visibleTestCases.some(testCase => isSameTestCase(selected, testCase))));
                                } else {
                                    setCheckedCases(current => [...current, ...visibleTestCases.filter(testCase => !current.some(selected => isSameTestCase(selected, testCase)))]);
                                }
                            }
                        }}
                        label={""}
                    />
                    <CustomSearch
                        id="1"
                        name={"searchField"}
                        placeholder={"Search for a test case"}
                        disabled={false}
                        onChange={handleSearch} />
                </SearchRow>
            </TestCaseHeadingWrapper>
            <CustomScrollbars autoHide autoHeight autoHeightMax={(windowSize.windowSize.innerHeight * .9) - 175}>
                <Phrases>
                    {testCases.map((name, idx) =>
                        <Phrase key={idx}>
                            <StyledCheckbox
                                checked={checkedCases.some(selected => isSameTestCase(selected, visibleTestCases[idx]))}
                                onClick={(e) => {
                                    const selectedTestCase = visibleTestCases[idx];
                                    if (checkedCases.some(selected => isSameTestCase(selected, selectedTestCase))) {
                                        setCheckedCases(current => current.filter(testCase => !isSameTestCase(testCase, selectedTestCase)));
                                    } else {
                                        setCheckedCases(current => [...current, selectedTestCase]);
                                    }
                                }}
                                label={""}
                            />
                            {isLoadingCase(name) ?
                                <TestCaseChip className={getTestCaseColor(name)} onClick={() => editTestCase(name, testCaseIds[idx])}>
                                    <SecondaryLoader />
                                </TestCaseChip>
                                :
                                <TestCaseChip className={getTestCaseColor(name)} onClick={() => editTestCase(name, testCaseIds[idx])}>
                                    {/* truncate name */}
                                    {name}
                                </TestCaseChip>
                            }
                            <TooltipWrapper place="top" text="Delete">
                                <div className="remove-thumbtack" onClick={() => {
                                    setNameToDelete(name);
                                    setContentItemIdToDelete(testCaseIds[idx])
                                }}>
                                    <img src={removeIcon} />
                                </div>
                            </TooltipWrapper>
                            <TooltipWrapper place="top" text="Copy">
                                <div className="remove-thumbtack" onClick={async () => {
                                    const testCase = await testCaseContainer.loadAppLevelTestCase(props.applicationId, name, testCaseIds[idx]);
                                    if (testCase) {
                                        testCase.name = testCase.name + "-copy";
                                    }
                                    updateAvailableTestCases(testCase);
                                }} >
                                    <img src={copyIcon} />
                                </div>
                            </TooltipWrapper>
                            <TooltipWrapper place="top" text="Run">
                                <div className="remove-thumbtack" onClick={() => {
                                    testCaseContainer.runAppLevelTestCases([visibleTestCases[idx]]);
                                }}>
                                    <img src={refresh} />
                                </div>
                            </TooltipWrapper>
                            <TooltipWrapper place="top" text="Try in simulator">
                                <div className="remove-thumbtack" onClick={async () => {
                                    await simulateCase(name, testCaseIds[idx]);
                                }}>
                                    <img src={arrowRight} />
                                </div>
                            </TooltipWrapper>
                        </Phrase>
                    )}
                </Phrases>
            </CustomScrollbars>
            {testCase != null ? <TestCaseEditingModal
                initialValue={testCase}
                onSave={async (tc) => {
                    await testCaseContainer.deleteAppLevelTestCase(props.applicationId, testCase.name, testCase?.contentItemId);
                    await testCaseContainer.createTestCase(props.applicationId, tc);
                    testCaseContainer.loadAppLevelTestCases(props.applicationId);
                    setTestCase(null);
                }} onClose={() => setTestCase(null)}
                isContentItemView={false} /> : <></>}

            {nameToDelete?.length ?
                <ConfirmationDialog title="Delete" deleteText="Yes, Delete"
                    isLoading={testCaseContainer.loading}
                    onClose={() => closeDeleteConfirmation()}
                    onConfirm={() => confirmDelete()}
                >
                    <DeleteConfirmation
                        itemLabel={` this test case? ${nameToDelete}`} />
                </ConfirmationDialog>
                :
                <></>
            }
            {casesToDelete.length ?
                <ConfirmationDialog title="Delete selected test cases" deleteText={`Delete ${casesToDelete.length} Test${casesToDelete.length === 1 ? "" : "s"}`}
                    isLoading={isBulkDeleting}
                    disabled={deleteConfirmationText !== "DELETE"}
                    onClose={closeBulkDeleteConfirmation}
                    onConfirm={confirmBulkDelete}
                >
                    <BulkDeletePrompt>
                        <p><strong>Warning:</strong> You are about to permanently delete {casesToDelete.length} selected test case{casesToDelete.length === 1 ? "" : "s"}. This cannot be undone.</p>
                        <label htmlFor="bulk-delete-test-cases">Type DELETE to confirm</label>
                        <input id="bulk-delete-test-cases" type="text" value={deleteConfirmationText}
                            onChange={(event) => setDeleteConfirmationText(event.target.value)} autoFocus />
                        {bulkDeleteError && <p role="alert">{bulkDeleteError}</p>}
                    </BulkDeletePrompt>
                </ConfirmationDialog>
                : <></>
            }
        </div>
    )
}

const SearchRow = styled.div`
    display: flex;
    flex-direction: row;`;
const TestCaseHeadingWrapper = styled.div`
    padding: 0 20px 0px 24px;
`;
const BulkDeletePrompt = styled.div`
    padding: 32px;
    font-family: Muli;
    color: ${color_text_default};
    p {
        margin: 0 0 24px;
        line-height: 24px;
    }
    label {
        display: block;
        margin-bottom: 8px;
    }
    input {
        box-sizing: border-box;
        width: 100%;
        padding: 10px 12px;
        border: 1px solid ${silver_four};
        border-radius: 8px;
        font: inherit;
    }
    [role="alert"] {
        margin-top: 16px;
        color: ${color_colors_decline};
    }
`;

const Phrases = styled.div`
    padding: 0 24px;
    padding-bottom: 40px;
`;

const Phrase = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    width: 100%;
    margin: 8px 0;
    cursor: pointer;
    .remove-thumbtack {
        width: 24px;
        margin-left: 8px;
        img {
            width: 100%;
            margin-top: 10px;
        }
    }
    &:first-of-type {
        margin-top: 16px;
    }
    &:last-of-type {
        margin-bottom: 16px;
    }
`;

const CustomSearch = styled(SearchField)`    
    margin-bottom: 0px;
    div > .field-container {
        height: 32px;
        box-shadow: none;
        border-radius: 8px;
    }
`;

const StyledTopButton = styled(Button)`
    margin-top: 0px;
    margin-bottom: 8px;
    margin-right: 8px;
    margin-left: 0px;
`;

const TestCaseChip = styled.div`
    &.red {
        background: ${pastel_red};
    }
    &.green {
        background: ${pastel_green};
    }
    overflow: hidden;
    background: white;
    border: 1px solid ${silver_four};
    border-radius: 8px;
    width: 100%;
    padding: 6px 16px;    
    font-size: 14px;
    line-height: 20px;
    font-weight: 400;
    .spinner > div {
        background-color: ${color_colors_ocean};
    }
    .spinner {
        height: 24px;
        margin-top: -3px;
    }
`;

const StyledCheckbox = styled(CheckboxField)`
    margin-top: 4px;
`
const SearchCheckbox = styled(CheckboxField)`
    margin-top: 12px;
`
const containerStyle = css`
    padding-top: 32px;
    max-width: 432px;
    font-family: Muli;
    font-style: normal;
    font-weight: normal;
    .title-container {
        .title {
            font-size: 18px;
            line-height: 24px;
            padding-bottom: 18px;
            color: ${color_text_default}
        }
        .subtitle {
            font-size: 14px;
            line-height: 20px;
            padding-bottom: 12px;
            color: ${color_text_light}
        }
    }
`;

const RestartButton = styled(StyledTopButton)`
    color: ${color_colors_growth};
`;

export default SimulatorTestCasesPanel
